import ImageIcon from "@redotech/redo-icons/image-01_filled.svg";
import LightningIcon from "@redotech/redo-icons/lightning-01_filled.svg";
import UploadIcon from "@redotech/redo-icons/upload-01_filled.svg";
import { ProductVisualizationUploadMessageType } from "@redotech/redo-model/conversation/message-parsers/product-visualization-upload-message-parser";
import { domAnimation, LazyMotion } from "motion/react";
import * as m from "motion/react-m";
import { memo, useEffect, useState } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { getLocalStorageWithExpiry } from "../../utils/local-storage-wrapper";
import { TRY_IT_ON_PRODUCT_KEY } from "../../utils/shared-conf";
import { ChatContentRenderer } from "./chat-content-renderer";
import { GenericRejectedState } from "./chat-content-states";
import { ImageLightbox } from "./image-lightbox";
import * as productVisualizationUploadCss from "./product-visualization-upload.module.css";

export type HandleVisualizationUploadFn = (params: {
  conversationId: string;
  messageId: string;
  imageUrl: string;
  imageHash: string;
  imageExtension: string;
  pendingToolCallId?: string;
  productHandle?: string;
  variantId?: string;
}) => Promise<void>;

export type UploadVisualizationImageFn = (params: {
  conversationId: string;
  toolCallId: string;
  imageData: string;
  contentType: string;
}) => Promise<{ imageUrl: string; imageHash: string; imageExtension: string }>;

export const ProductVisualizationUpload = memo(
  function ProductVisualizationUpload({
    message,
    readOnly,
    onImageUpload,
    conversationId,
    uploadImage,
  }: {
    message: ProductVisualizationUploadMessageType;
    readOnly: boolean;
    onImageUpload?: HandleVisualizationUploadFn;
    conversationId: string;
    uploadImage?: UploadVisualizationImageFn;
  }) {
    const [uploadPhase, setUploadPhase] = useState<
      "uploading" | "generating" | null
    >(null);
    const [error, setError] = useState<string | null>(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(
      null,
    );

    const handleFileSelect = async (file: File) => {
      if (!onImageUpload || !uploadImage || readOnly) return;

      setUploadPhase("uploading");
      setError(null);

      try {
        const base64Data = await fileToBase64(file);
        const { imageUrl, imageHash, imageExtension } = await uploadImage({
          conversationId,
          toolCallId: message.pendingToolCallId ?? "",
          imageData: base64Data,
          contentType: file.type,
        });

        // Store the uploaded image URL to display during generating phase
        setUploadedImageUrl(imageUrl);
        setUploadPhase("generating");

        // For temp conversations, get productHandle and variantId from localStorage
        // For existing conversations, these will be undefined (which is correct)
        const productData =
          conversationId === "temp"
            ? getLocalStorageWithExpiry(TRY_IT_ON_PRODUCT_KEY)
            : null;

        // Confirm the tool call with the uploaded image hash and extension
        await onImageUpload({
          conversationId,
          messageId: message._id,
          imageUrl,
          imageHash,
          imageExtension,
          pendingToolCallId: message.pendingToolCallId ?? undefined,
          productHandle: productData?.productHandle,
          variantId: productData?.variantId,
        });

        // Clean up localStorage after successful upload
        if (productData) {
          localStorage.removeItem(TRY_IT_ON_PRODUCT_KEY);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to upload image");
      } finally {
        setUploadPhase(null);
      }
    };

    // Get the uploaded image URL from customerInputContent for confirmed state
    const confirmedImageUrl = message.customerInputContent?.imageUrl;

    return (
      <>
        <Flex align="flex-end" dir="column" w="full">
          <Flex dir="column" style={{ maxWidth: "400px", width: "100%" }}>
            <ChatContentRenderer
              contentByState={{
                pending: (
                  <ImageUploadInput
                    error={error}
                    onFileSelect={handleFileSelect}
                    readOnly={readOnly}
                    uploadedImageUrl={uploadedImageUrl}
                    uploadPhase={uploadPhase}
                  />
                ),
                confirmed: confirmedImageUrl ? (
                  <Flex align="flex-end" dir="column" w="full">
                    <img
                      alt="Your uploaded image"
                      onClick={() => setLightboxOpen(true)}
                      src={confirmedImageUrl}
                      style={{
                        maxWidth: "280px",
                        height: "auto",
                        borderRadius: "8px",
                        display: "block",
                        cursor: "zoom-in",
                      }}
                    />
                  </Flex>
                ) : null,
                rejected: (
                  <GenericRejectedState title="Unable to generate visualization" />
                ),
              }}
              message={message}
            />
          </Flex>
        </Flex>
        {lightboxOpen && confirmedImageUrl && (
          <ImageLightbox
            altText="Your uploaded image (full size)"
            imageUrl={confirmedImageUrl}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </>
    );
  },
);

const UploadingAnimation = memo(function UploadingAnimation() {
  return (
    <m.div
      animate={{ opacity: 1, y: 0, scale: 1 }}
      initial={{ opacity: 0, y: 10, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
    >
      <Flex align="center" dir="row" gap="xs">
        <UploadIcon className={productVisualizationUploadCss.uploadIcon} />
        <Text
          className={productVisualizationUploadCss.uploadingText}
          fontSize="sm"
        >
          Uploading image
        </Text>
      </Flex>
    </m.div>
  );
});

const GeneratingAnimation = memo(function GeneratingAnimation({
  uploadedImageUrl,
}: {
  uploadedImageUrl: string | null;
}) {
  const [progress, setProgress] = useState(0);

  // Animate progress from 0 to 99% over 45 seconds
  useEffect(() => {
    const startTime = Date.now();
    const duration = 45_000; // 45 seconds
    const targetProgress = 99;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const currentProgress = Math.min(
        (elapsed / duration) * targetProgress,
        targetProgress,
      );
      setProgress(Math.floor(currentProgress));

      if (currentProgress >= targetProgress) {
        clearInterval(interval);
      }
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(interval);
  }, []);

  return (
    <m.div
      animate={{ opacity: 1, y: 0, scale: 1 }}
      initial={{ opacity: 0, y: 10, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 500, damping: 30 }}
    >
      <Flex align="center" dir="column" gap="xs" w="full">
        {uploadedImageUrl && (
          <img
            alt="Your uploaded image"
            src={uploadedImageUrl}
            style={{
              maxWidth: "280px",
              height: "auto",
              borderRadius: "8px",
              display: "block",
            }}
          />
        )}
        <Flex
          align="center"
          dir="row"
          gap="xs"
          style={{ marginTop: uploadedImageUrl ? "16px" : "0" }}
        >
          <LightningIcon className={productVisualizationUploadCss.lightning} />
          <Text
            className={productVisualizationUploadCss.generatingText}
            fontSize="sm"
          >
            Generating visualization
          </Text>
        </Flex>
        <Text
          fontSize="xs"
          style={{ fontSize: "11px", marginTop: "2px" }}
          textColor="tertiary"
        >
          This may take up to 45 seconds
        </Text>
        <Flex
          align="center"
          dir="row"
          gap="sm"
          style={{ width: "100%", marginTop: "8px" }}
        >
          <div
            className={productVisualizationUploadCss.progressBar}
            style={{ flex: 1 }}
          >
            <div
              className={productVisualizationUploadCss.progressFill}
              style={{ width: `${progress}%` }}
            >
              <div className={productVisualizationUploadCss.progressShimmer} />
            </div>
          </div>
          <Text
            className={productVisualizationUploadCss.progressText}
            fontSize="xs"
            style={{ minWidth: "36px", textAlign: "right" }}
            textColor="secondary"
          >
            {progress}%
          </Text>
        </Flex>
      </Flex>
    </m.div>
  );
});

const ImageUploadInput = memo(function ImageUploadInput({
  onFileSelect,
  uploadPhase,
  error,
  readOnly,
  uploadedImageUrl,
}: {
  onFileSelect: (file: File) => void;
  uploadPhase: "uploading" | "generating" | null;
  error: string | null;
  readOnly: boolean;
  uploadedImageUrl: string | null;
}) {
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isHovering, setIsHovering] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    // Clear any previous validation errors
    setValidationError(null);

    // Validate file type
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setValidationError(
        `Invalid file type. Please upload ${validTypes.join(", ")}`,
      );
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setValidationError("File too large. Maximum size is 10MB");
      return;
    }

    onFileSelect(file);
  };

  if (readOnly) {
    return (
      <Flex dir="column" gap="xs">
        <Text fontSize="sm">Upload your photo to see the visualization</Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="sm" style={{ width: "100%" }}>
      <label
        htmlFor="file-upload"
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{
          border: uploadPhase
            ? "none"
            : dragActive
              ? "2px dashed #4A90E2"
              : "2px dashed rgba(0, 0, 0, 0.2)",
          borderRadius: "8px",
          padding: "24px",
          textAlign: "center",
          cursor: uploadPhase ? "not-allowed" : "pointer",
          backgroundColor:
            dragActive || isHovering ? "var(--neutral-100)" : "transparent",
          transition: "all 0.2s ease",
        }}
      >
        <input
          accept="image/png,image/jpeg,image/jpg,image/webp"
          disabled={!!uploadPhase}
          id="file-upload"
          onChange={handleChange}
          style={{ display: "none" }}
          type="file"
        />
        <LazyMotion features={domAnimation}>
          <Flex align="center" dir="column" gap="xs">
            {uploadPhase === "generating" ? (
              <GeneratingAnimation
                key="generating"
                uploadedImageUrl={uploadedImageUrl}
              />
            ) : uploadPhase === "uploading" ? (
              <UploadingAnimation key="uploading" />
            ) : (
              <>
                <ImageIcon
                  style={{
                    width: "32px",
                    height: "32px",
                    color: "var(--neutral-500)",
                    marginBottom: "8px",
                  }}
                />
                <Text fontSize="sm" fontWeight="medium" textColor="secondary">
                  Drag & drop or click to upload
                </Text>
                <Text fontSize="xs" textColor="tertiary">
                  PNG, JPG, WEBP • Max 10MB
                </Text>
              </>
            )}
          </Flex>
        </LazyMotion>
      </label>
      {(error || validationError) && (
        <Text fontSize="xs" textColor="error">
          {validationError || error}
        </Text>
      )}
    </Flex>
  );
});

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URI prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(",")[1];
      if (base64) {
        resolve(base64);
      } else {
        reject(new Error("Failed to convert file to base64"));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}
